import { useState, useEffect, useCallback } from 'preact/hooks'
import type { CloudNote } from '../../types'
import { listCloudNotes, upsertCloudNote, deleteCloudNote } from '../../services/api'
import { formatDate, EmptyState, DetailRow, cardStyle, detailPanelStyle, listAreaStyle, refreshBtnStyle } from './shared'
import { ConfirmDialog, ModalDialog, FormField, SaveButton, DeleteButton, CreateButton, InlineInput } from './CrudHelpers'

/** Alert note type = 5 (matches NoteType.alert in the Flutter app) */
const ALERT_TYPE = 5

/** Parse JSON content; returns null if not valid JSON */
function parseContent(content?: string): Record<string, unknown> | null {
  if (!content) return null
  try {
    const p = JSON.parse(content)
    return typeof p === 'object' && p !== null ? p : null
  } catch {
    return null
  }
}

/** Read enabled flag from parsed content; defaults to true (matches Flutter) */
function isAlertEnabled(note: CloudNote): boolean {
  const p = parseContent(note.content)
  if (!p) return true
  return p.enabled !== false
}

/** Owner starts with "task_" — these are task-derived alerts and excluded from the list */
function isTaskAlert(note: CloudNote): boolean {
  const meta = note.metadata as Record<string, unknown> | undefined
  const owner = (meta?.owner as string | undefined) ?? (meta?.persona_id as string | undefined)
  return typeof owner === 'string' && owner.startsWith('task_')
}

/** Get scheduled time as ms; uses content date/time, falls back to metadata.scheduled_at */
function getScheduledTime(note: CloudNote): number {
  const p = parseContent(note.content)
  if (p && p.date) {
    const t = Date.parse(`${p.date as string} ${(p.time as string) || '00:00'}`)
    if (!isNaN(t)) return t
  }
  const meta = note.metadata as Record<string, unknown> | undefined
  const scheduled = meta?.scheduled_at as string | undefined
  if (scheduled) {
    const t = Date.parse(scheduled)
    if (!isNaN(t)) return t
  }
  return 0
}

export function AlertsView({ focusId }: { focusId?: string } = {}) {
  const [notes, setNotes] = useState<CloudNote[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedNote, setSelectedNote] = useState<CloudNote | null>(null)
  const [editingNote, setEditingNote] = useState<CloudNote | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setNotes(await listCloudNotes())
    } catch (e) {
      console.error('Failed to load alerts:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Auto-select the focused alert (deep-link from search).
  useEffect(() => {
    if (!focusId || notes.length === 0) return
    const match = notes.find((n) => n.id === focusId)
    if (match && selectedNote?.id !== match.id) {
      setSelectedNote(match)
      setEditingNote({ ...match })
      setIsDirty(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, notes])

  const alerts = notes.filter((n) => n.type === ALERT_TYPE && !isTaskAlert(n))
  const active = alerts
    .filter((n) => isAlertEnabled(n))
    .sort((a, b) => getScheduledTime(a) - getScheduledTime(b))
  const disabled = alerts
    .filter((n) => !isAlertEnabled(n))
    .sort((a, b) => getScheduledTime(a) - getScheduledTime(b))

  const handleSelect = (note: CloudNote) => {
    if (selectedNote?.id === note.id) {
      setSelectedNote(null)
      setEditingNote(null)
      setIsDirty(false)
    } else {
      setSelectedNote(note)
      setEditingNote({ ...note })
      setIsDirty(false)
    }
  }

  const updateField = (field: string, value: unknown) => {
    if (!editingNote) return
    setEditingNote({ ...editingNote, [field]: value })
    setIsDirty(true)
  }

  const handleSave = async () => {
    if (!editingNote) return
    setSaving(true)
    try {
      const updated = { ...editingNote, last_updated_at: new Date().toISOString() }
      await upsertCloudNote(updated)
      setNotes((prev) => prev.map((n) => n.id === updated.id ? updated : n))
      setSelectedNote(updated)
      setEditingNote({ ...updated })
      setIsDirty(false)
    } catch (e) {
      console.error('Failed to save alert:', e)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedNote) return
    try {
      await deleteCloudNote(selectedNote.id)
      setNotes((prev) => prev.filter((n) => n.id !== selectedNote.id))
      setSelectedNote(null)
      setEditingNote(null)
      setConfirmDelete(false)
    } catch (e) {
      console.error('Failed to delete alert:', e)
    }
  }

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Alert list */}
      <div style={listAreaStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid rgba(250,250,250,0.1)' }}>
          <span style={{ flex: 1, fontSize: 13, color: 'rgba(250,250,250,0.5)' }}>
            {alerts.length} alerts ({active.length} active)
          </span>
          <CreateButton label="New Alert" onClick={() => setShowCreate(true)} />
          <button onClick={load} style={refreshBtnStyle} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading...</div>}
          {!loading && alerts.length === 0 && <EmptyState label="No alerts found" />}

          {!loading && active.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(250,250,250,0.5)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                Active ({active.length})
              </div>
              {active.map((note) => (
                <AlertCard key={note.id} note={note} selected={selectedNote?.id === note.id} onSelect={handleSelect} />
              ))}
            </div>
          )}

          {!loading && disabled.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(250,250,250,0.5)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                Disabled ({disabled.length})
              </div>
              {disabled.map((note) => (
                <AlertCard key={note.id} note={note} selected={selectedNote?.id === note.id} onSelect={handleSelect} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Editable detail panel */}
      {editingNote && (
        <div style={detailPanelStyle}>
          <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: 'rgba(250,250,250,0.5)' }}>Edit Alert</span>
            <SaveButton dirty={isDirty} saving={saving} onClick={handleSave} />
            <DeleteButton onClick={() => setConfirmDelete(true)} />
            <button onClick={() => { setSelectedNote(null); setEditingNote(null); setIsDirty(false) }} style={refreshBtnStyle}>X</button>
          </div>
          <AlertDetailFields editingNote={editingNote} updateField={updateField} />
        </div>
      )}

      {/* Create alert modal */}
      {showCreate && (
        <CreateAlertModal
          onClose={() => setShowCreate(false)}
          onCreate={async (note) => {
            await upsertCloudNote(note)
            setNotes((prev) => [note, ...prev])
            setShowCreate(false)
          }}
        />
      )}

      {/* Delete confirm */}
      {confirmDelete && selectedNote && (
        <ConfirmDialog
          message={`Delete alert "${selectedNote.title || 'Untitled'}"?`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  )
}

// ── Alert Detail Fields (parses JSON content) ─────────────────────

function AlertDetailFields({ editingNote, updateField }: {
  editingNote: CloudNote
  updateField: (field: string, value: unknown) => void
}) {
  const parsed = parseContent(editingNote.content)

  // Helper to update a key inside the parsed content JSON
  const updateContent = (key: string, value: unknown) => {
    const current = parseContent(editingNote.content) || {}
    if (value === undefined || value === '') delete current[key]
    else current[key] = value
    updateField('content', JSON.stringify(current))
  }

  const inputStyle: Record<string, string | number> = {
    width: '100%', padding: '8px 10px', background: 'rgba(0,34,51,0.8)',
    border: '1px solid rgba(250,250,250,0.15)', borderRadius: 6,
    color: '#FAFAFA', fontSize: 13, boxSizing: 'border-box',
  }

  // Format recurrence_rule object as a human summary
  const formatRecurrence = (rule: unknown): string | null => {
    if (!rule || typeof rule !== 'object') return null
    const r = rule as Record<string, unknown>
    const parts: string[] = []
    if (r.frequency) parts.push(String(r.frequency))
    if (r.interval && Number(r.interval) !== 1) parts.push(`every ${r.interval}`)
    if (Array.isArray(r.week_days) && r.week_days.length > 0) {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const days = (r.week_days as number[]).map((d) => dayNames[d] || String(d)).join(', ')
      parts.push(`on ${days}`)
    }
    return parts.length > 0 ? parts.join(' ') : null
  }

  // If content isn't valid JSON, show plain textarea fallback
  if (!parsed) {
    return (
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Title</label>
          <InlineInput value={editingNote.title || ''} onChange={(v) => updateField('title', v)} placeholder="Alert title" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Content</label>
          <InlineInput type="textarea" value={editingNote.content || ''} onChange={(v) => updateField('content', v)} placeholder="Content" />
        </div>
        <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
          <DetailRow label="Created" value={formatDate(editingNote.created_at)} />
          <DetailRow label="Updated" value={formatDate(editingNote.last_updated_at)} />
        </div>
      </div>
    )
  }

  const recurrenceLabel = formatRecurrence(parsed.recurrence_rule)
  const enabled = parsed.enabled !== false

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <label style={fieldLabelStyle}>Title</label>
        <InlineInput
          value={(parsed.title as string) || editingNote.title || ''}
          onChange={(v) => {
            updateContent('title', v)
            updateField('title', v)
          }}
          placeholder="Alert title"
        />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={fieldLabelStyle}>Note</label>
        <InlineInput
          type="textarea"
          value={(parsed.note as string) || ''}
          onChange={(v) => updateContent('note', v)}
          placeholder="Optional note"
        />
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={fieldLabelStyle}>Date</label>
          <input
            type="date"
            style={inputStyle}
            value={(parsed.date as string) || ''}
            onInput={(e) => updateContent('date', (e.target as HTMLInputElement).value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={fieldLabelStyle}>Time</label>
          <input
            type="time"
            style={inputStyle}
            value={((parsed.time as string) || '').slice(0, 5)}
            onInput={(e) => updateContent('time', (e.target as HTMLInputElement).value)}
          />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(250,250,250,0.7)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={() => updateContent('enabled', !enabled)}
          />
          Enabled
        </label>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(250,250,250,0.7)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={parsed.acknowledged === true}
            onChange={() => updateContent('acknowledged', parsed.acknowledged !== true)}
          />
          Acknowledged
        </label>
      </div>
      {recurrenceLabel && <DetailRow label="Recurrence" value={recurrenceLabel} />}
      {parsed.trigger_type !== undefined && <DetailRow label="Trigger" value={String(parsed.trigger_type)} />}
      {(parsed.occurrence_count as number) > 0 && <DetailRow label="Occurrences" value={String(parsed.occurrence_count)} />}
      {parsed.persona_name && <DetailRow label="Persona" value={String(parsed.persona_name)} />}
      <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
        <DetailRow label="Created" value={formatDate(editingNote.created_at)} />
        <DetailRow label="Updated" value={formatDate(editingNote.last_updated_at)} />
      </div>
    </div>
  )
}

// ── Alert Card ────────────────────────────────────────────────────

function AlertCard({ note, selected, onSelect }: { note: CloudNote; selected: boolean; onSelect: (n: CloudNote) => void }) {
  const parsed = parseContent(note.content)
  const meta = note.metadata as Record<string, unknown> | undefined
  const enabled = isAlertEnabled(note)
  const scheduledMs = getScheduledTime(note)
  const isOverdue = enabled && scheduledMs > 0 && scheduledMs < Date.now()
  const title = (parsed?.title as string) || note.title || 'Untitled'
  const personaName = parsed?.persona_name as string | undefined

  let scheduledLabel: string | null = null
  if (parsed?.date) {
    scheduledLabel = `${parsed.date as string}${parsed.time ? ` ${parsed.time as string}` : ''}`
  } else if (meta?.scheduled_at) {
    scheduledLabel = formatDate(meta.scheduled_at as string)
  }

  const accent = !enabled ? '#666' : isOverdue ? '#ff9800' : '#f44336'

  return (
    <div
      onClick={() => onSelect(note)}
      style={{
        ...cardStyle,
        cursor: 'pointer',
        borderLeft: `3px solid ${accent}`,
        background: selected ? 'rgba(45,165,160,0.15)' : cardStyle.background,
        opacity: enabled ? 1 : 0.6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, fontWeight: 500, color: enabled ? '#FAFAFA' : 'rgba(250,250,250,0.5)' }}>{title}</span>
        {personaName && (
          <span style={{ fontSize: 10, padding: '1px 4px', background: 'rgba(45,165,160,0.3)', borderRadius: 2, color: '#FAFAFA' }}>
            {personaName}
          </span>
        )}
        {!enabled && (
          <span style={{ fontSize: 10, padding: '1px 4px', background: '#555', borderRadius: 2, color: '#fff' }}>Disabled</span>
        )}
        {enabled && isOverdue && (
          <span style={{ fontSize: 10, padding: '1px 4px', background: '#ff9800', borderRadius: 2, color: '#fff' }}>Overdue</span>
        )}
      </div>
      {scheduledLabel && (
        <div style={{ fontSize: 12, color: isOverdue ? '#ff9800' : 'rgba(250,250,250,0.5)', marginTop: 4 }}>
          {scheduledLabel}
        </div>
      )}
    </div>
  )
}

// ── Create Alert Modal ────────────────────────────────────────────

function CreateAlertModal({ onClose, onCreate }: {
  onClose: () => void
  onCreate: (note: CloudNote) => Promise<void>
}) {
  const [title, setTitle] = useState('')
  const [note, setNote] = useState('')
  const [schedDate, setSchedDate] = useState('')
  const [schedTime, setSchedTime] = useState('')
  const [recurrence, setRecurrence] = useState('')
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    if (!title.trim()) return
    setCreating(true)
    try {
      const now = new Date().toISOString()
      const scheduledAt = schedDate ? new Date(`${schedDate}T${schedTime || '00:00'}`).toISOString() : undefined
      const contentObj: Record<string, unknown> = {
        title: title.trim(),
        enabled: true,
        acknowledged: false,
      }
      if (note.trim()) contentObj.note = note.trim()
      if (schedDate) contentObj.date = schedDate
      if (schedTime) contentObj.time = schedTime
      const newNote: CloudNote = {
        id: crypto.randomUUID(),
        title: title.trim(),
        content: JSON.stringify(contentObj),
        type: ALERT_TYPE,
        metadata: {
          scheduled_at: scheduledAt,
          recurrence: recurrence || undefined,
        },
        created_at: now,
        last_updated_at: now,
      }
      await onCreate(newNote)
    } catch (e) {
      console.error('Failed to create alert:', e)
    } finally {
      setCreating(false)
    }
  }

  return (
    <ModalDialog
      title="New Alert"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: 'rgba(250,250,250,0.1)', color: 'rgba(250,250,250,0.7)', cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!title.trim() || creating}
            style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: '#2DA5A0', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: !title.trim() || creating ? 0.5 : 1 }}
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </>
      }
    >
      <FormField label="Title" value={title} onChange={setTitle} required placeholder="Alert title" />
      <FormField label="Note" value={note} onChange={setNote} type="textarea" placeholder="Optional note" />
      <FormField label="Date" value={schedDate} onChange={setSchedDate} type="date" />
      <FormField label="Time" value={schedTime} onChange={setSchedTime} type="time" />
      <FormField label="Recurrence" value={recurrence} onChange={setRecurrence} placeholder="e.g. daily, weekly" />
    </ModalDialog>
  )
}

const fieldLabelStyle: Record<string, string | number> = {
  fontSize: 12,
  color: 'rgba(250,250,250,0.5)',
  marginBottom: 4,
  display: 'block',
}
